import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, SearchBar, Spinner } from './shared';

export default function AdminInstrumentProfiles() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => { loadItems(); }, []);

    const loadItems = async () => {
        try {
            setLoading(true);
            const res = await adminApi.listInstrumentProfiles({
                query: searchQuery || undefined,
                limit: 200,
            });
            setItems(res.items || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load profiles');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Instrument Profiles</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {items.length} profile{items.length !== 1 ? 's' : ''} — curated instrument metadata
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => navigate('/admin/instrument-profiles/new')}>
                        New Profile
                    </Button>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                onSearch={loadItems}
                onRefresh={loadItems}
                placeholder="Search canonical_id or symbol..."
                className="mb-4"
            />

            {loading ? (
                <Spinner size="lg" text="Loading profiles..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No instrument profiles yet</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Canonical ID</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Asset Class</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Symbol</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Venue</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Rev</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Tags</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Sectors</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500">Priority</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((it) => (
                                <tr
                                    key={it.instrument_id}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => navigate(`/admin/instrument-profiles/${encodeURIComponent(it.instrument_id)}`)}
                                >
                                    <td className="px-4 py-3 font-mono text-xs">{it.instrument_id}</td>
                                    <td className="px-3 py-3"><Badge variant="outline">{it.asset_class}</Badge></td>
                                    <td className="px-3 py-3 font-semibold">{it.symbol}</td>
                                    <td className="px-3 py-3 text-gray-500">{it.venue || '-'}</td>
                                    <td className="px-3 py-3 text-center text-gray-500 font-mono">{it.rev}</td>
                                    <td className="px-3 py-3">
                                        {(it.tags || []).length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {(it.tags || []).slice(0, 3).map((t) => (
                                                    <Badge key={t} variant="info">#{t}</Badge>
                                                ))}
                                            </div>
                                        ) : <span className="text-gray-300">-</span>}
                                    </td>
                                    <td className="px-3 py-3 text-xs text-gray-500">
                                        {(it.sectors || []).join(', ') || '-'}
                                    </td>
                                    <td className="px-3 py-3 text-right text-xs text-gray-500">{it.watchlist_priority || '-'}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="ghost"
                                                size="xs"
                                                onClick={() => navigate(`/admin/instrument-profiles/${encodeURIComponent(it.instrument_id)}/edit`)}
                                            >
                                                Edit
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
